<template>
    <RedesignBaseCard
        :border-color="statusColor"
        :border-width="2"
        with-border
    >
        <template #content>
            <div
                class="redesign-extension-card"
                :class="{ 'is-alert-active': isAlertActive, 'no-animation': isVisualAlertDisabled }"
                :style="alertStyle"
            >
                <!-- Header Section -->
                <RedesignExtensionCardHeader
                    :user-name="extension.userName"
                    :status-text="extensionStatusData.text"
                    :status-icon="extensionStatusData.iconName"
                    :status-color="statusColor"
                    :font-size="agentNameFontSize"
                    :cards-cols="cardsCols"
                    :show-statistics="showStatistics"
                    :is-content-hidden="showAllCalls"
                    :timer-display-time="timerDisplayTime"
                    :timer-color="timerColor"
                    :timer-font-size="timerFontSize"
                    :is-alert-active="isAlertActive"
                    :alert-icon-bg-color="iconTimerFillColor"
                    :statistics="headerStatistics"
                    :extension="extension"
                    @toggle-summary="summaryOpened = $event"
                />

                <!-- Body Section -->
                <RedesignExtensionCardBody
                    :cards-cols="cardsCols"
                    :show-statistics="showStatistics"
                    :show-summary="summaryOpened"
                    :show-all-calls="showAllCalls"
                    :show-total-calls-summary="showTotalCallsSummary"
                    :settings="settings"
                    :statistics="bodyStatistics"
                    :total-calls-statistic="totalCallsStatistic"
                    :calls="calls"
                    @toggle-show-all-calls="showAllCalls = $event"
                />
            </div>
        </template>
    </RedesignBaseCard>
</template>

<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue'
import { useTimer } from '@/composables/useTimer'
import { useExtensionCardAlerts } from '@/composables/useExtensionCardAlerts'
import { useExtensionCardStatistics } from '@/composables/useExtensionCardStatistics'
import useExtensionStatus from '@/composables/useExtensionStatus'
import RedesignBaseCard from '@/components/RedesignBaseComponents/RedesignBaseCard.vue'
import RedesignExtensionCardHeader from '@/components/RedesignComponents/Cards/ExtensionCard/RedesignExtensionCardHeader.vue'
import RedesignExtensionCardBody from '@/components/RedesignComponents/Cards/ExtensionCard/RedesignExtensionCardBody.vue'
import { realTimeSettings } from '@/enum/defaultWidgetSettings'
import { Settings } from '@/types/dashboard'
import { Extension } from '@/types/extension'
import { isVisualAlertDisabled } from '@/composables/globalData/useLayouts'
import { shouldEmitRecentlyLoggedOutExpired } from '@/composables/useExpiredRecentlyLoggedOutKeys'

/* Props */
const props = withDefaults(
    defineProps<{
        extension: Extension
        settings?: Settings
        cardsCols: number
    }>(),
    {
        settings: () => ({})
    }
)

/* Emits */
const emit = defineEmits<{
    (event: 'recently-logged-out-expired'): void
}>()

// Local UI state
const summaryOpened = ref(false)
const showAllCalls = ref(false)

// Create reactive refs
const extensionRef = toRef(props, 'extension')
const cardsColsRef = toRef(props, 'cardsCols')

// Merged settings
const extensionSettings = computed(() => ({
    ...realTimeSettings,
    ...props.settings
}))

// Timer composable
const { timerDisplayTime, startTimer, timerState } = useTimer()

// Extension status
const { extensionStatusData } = useExtensionStatus(extensionRef)

// Status color (for card border)
const statusColor = computed(() => extensionStatusData.value.color)

// Alert composable
const {
    alertColor,
    iconTimerFillColor,
    timerColor
} = useExtensionCardAlerts({
    extension: extensionRef,
    timerState,
    timerDisplayTime,
    statusColor
})

// Statistics composable
const {
    headerStatistics,
    bodyStatistics,
    totalCallsStatistic
} = useExtensionCardStatistics({
    extension: extensionRef,
    settings: extensionSettings,
    cardsCols: cardsColsRef
})

// Computed properties
const agentNameFontSize = computed(() => +extensionSettings.value.agentNameFontSize)
const timerFontSize = computed(() => +extensionSettings.value.timerFontSize)
const showStatistics = computed(() => !props.settings.hideStatistics)
const showTotalCallsSummary = computed(() => !!props.settings.showTotalCallsSummary)
const isAlertActive = computed(() => !!alertColor.value)
const calls = computed(() => props.extension.calls || [])

// Alert style for blinking background
const alertStyle = computed(() => {
    if (alertColor.value) {
        return {
            '--alert-color': alertColor.value,
            '--alert-color-light': alertColor.value + '0D',
            '--alert-color-medium': alertColor.value + '1A'
        }
    }
    return {}
})

// Reset showAllCalls when no calls
watch(() => calls.value.length, (value) => {
    if (!value && showAllCalls.value) {
        showAllCalls.value = false
    }
})

watch(() => timerState.value, (val) => {
    if (shouldEmitRecentlyLoggedOutExpired(
        extensionSettings.value,
        extensionRef.value.representativeStatus,
        val
    )) {
        emit('recently-logged-out-expired')
    }
})

// Start timer
startTimer(extensionRef, extensionSettings)
</script>

<style lang="scss">
.redesign-extension-card {
    background: var(--mainBgColor-color);

    &.is-alert-active {
        animation: blink-card-bg 1.5s ease-in-out infinite;
    }
    &.no-animation {
      animation: none;
      background-color: var(--alert-color-medium);
    }
}

@keyframes blink-card-bg {
    0%, 100% {
        background-color: var(--alert-color-light);
    }
    50% {
        background-color: var(--alert-color-medium);
    }
}
</style>
